/**
 * Artifact Card Registry
 *
 * Each entry is the single source of truth for shared (API-serializable) data:
 * capabilities, slot definitions, and preview config.
 * The frontend ArtifactCardRenderer extends these with React component references.
 * Table columns, bundle config, and allowed layouts are owned by presets.
 *
 * Canonical card types appear first. Deprecated types are kept at the end
 * so isValidRegistryId() still accepts them during the transition window
 * between DB migration and code deploy (migration: 20260326_migrate_card_types).
 */

import type { ArtifactCardEntry } from './core';
import { dealBriefSampleJson } from '../deal-brief-samples';

// ─────────────────────────────────────────────────────────────
// Shared capability presets
// ─────────────────────────────────────────────────────────────

const NO_CAPS = { supportsSelection: false, supportsKeyboardNav: false, supportsBulkActions: false } as const;

// ─────────────────────────────────────────────────────────────
// Shared table column definitions (consumed by PRESET_COMPONENTS)
// ─────────────────────────────────────────────────────────────

export const GENERIC_COLUMNS = {
  columns: [
    { key: 'unread', width: 'w-6' },
    { key: 'title', label: 'Title', flex: true, sortable: true },
    { key: 'category', label: 'Category', width: 'w-24', sortable: true },
    { key: 'status', label: 'Status', width: 'w-20', sortable: true },
    { key: 'updated', label: 'Updated', width: 'w-24', sortable: true },
    { key: 'actions', label: 'Actions', width: 'w-24', align: 'right' as const },
  ],
  rowActions: 'hover' as const,
  searchableFields: ['title'],
};

export const TRIAGE_COLUMNS = {
  columns: [
    { key: 'unread', width: 'w-6' },
    { key: 'avatar', width: 'w-10' },
    { key: 'title', label: 'Contact', flex: true, sortable: true, filterable: true },
    { key: 'subtitle', label: 'Context', flex: true },
    { key: 'actions', width: 'w-32', align: 'right' as const },
  ],
  rowActions: 'hover' as const,
  searchableFields: ['title', 'company', 'subtitle'],
};

export const OPS_MANIFEST = {
  columns: [
    { key: 'checkbox', width: 'w-8' },
    { key: 'avatar', width: 'w-10' },
    { key: 'title', label: 'Contact', flex: true, sortable: true },
    { key: 'stage', label: 'Status', width: 'w-24', sortable: true, filterable: true },
    { key: 'actions', width: 'w-40', align: 'right' as const },
  ],
  rowActions: 'always' as const,
  searchableFields: ['title', 'company'],
};

export const DASHBOARD_COLUMNS = {
  columns: [
    { key: 'title', label: 'Contact', flex: true, sortable: true },
    { key: 'company', label: 'Company', width: 'w-28', sortable: true, filterable: true },
    { key: 'priority', label: 'Priority', width: 'w-20', sortable: true, filterable: true },
    { key: 'stage', label: 'Stage', width: 'w-24', sortable: true, filterable: true },
    { key: 'date', label: 'Date', width: 'w-20', sortable: true },
    { key: 'actions', label: 'Actions', width: 'w-28', align: 'right' as const },
  ],
  rowActions: 'hover' as const,
  searchableFields: ['title', 'company', 'subtitle'],
};

const _SPLIT_PANEL_MANIFEST = {
  columns: [
    { key: 'unread', width: 'w-4' },
    { key: 'avatar', width: 'w-10' },
    { key: 'title', label: 'Contact', flex: true, sortable: true },
  ],
  rowActions: 'none' as const,
  searchableFields: ['title', 'company'],
};

export const QUICK_GLANCE_COLUMNS = {
  columns: [
    { key: 'status', width: 'w-6' },
    { key: 'title', label: 'Contact', flex: true, sortable: true },
    { key: 'actions', width: 'w-20', align: 'right' as const },
  ],
  rowActions: 'hover' as const,
  searchableFields: ['title', 'company', 'subtitle'],
};

const _ACTION_MANIFEST = {
  columns: [
    { key: 'unread', width: 'w-2' },
    { key: 'avatar', width: 'w-10' },
    { key: 'title', label: 'Contact', flex: true, sortable: true },
    { key: 'subtitle', label: 'Context', flex: true },
    { key: 'actions', width: 'w-48', align: 'right' as const },
  ],
  rowActions: 'always' as const,
  searchableFields: ['title', 'company', 'subtitle'],
};

// ─────────────────────────────────────────────────────────────
// Registry
// ─────────────────────────────────────────────────────────────

export const ARTIFACT_CARD_REGISTRY = [
  // ── Canonical card types ──────────────────────────────────
  {
    id: 'action_card', label: 'Action Card', description: 'Consolidated person-centric card for approval/triage flows with avatar, priority, and action buttons',
    supportsActions: true,
    rendersActionButtons: true,
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'link', 'company', 'avatar', 'priority'], mode: 'card' },
    tableColumns: {
      columns: [
        { key: 'title', label: 'Contact', flex: true, sortable: true },
        { key: 'company', label: 'Company', width: 'w-28', sortable: true },
        { key: 'subtitle', label: 'Role', width: 'w-28' },
        { key: 'priority', label: 'Priority', width: 'w-20', sortable: true },
        { key: 'stage', label: 'Status', width: 'w-24', sortable: true },
        { key: 'actions', label: 'Actions', width: 'w-28', align: 'right' as const },
      ],
      sampleRows: [
        { title: 'Rachel Green', company: 'Acme Corp', subtitle: 'VP of Sales', priority: 'high', stage: 'Needs Action' },
        { title: 'Marcus Chen', company: 'TechFlow Inc', subtitle: 'CTO', priority: 'medium', stage: 'Pending Review' },
        { title: 'Sarah Palmer', company: 'DataSync Labs', subtitle: 'Head of Ops', priority: 'high', stage: 'Needs Action' },
        { title: 'James Okafor', company: 'FinBridge', subtitle: 'CFO', priority: 'low', stage: 'Awaiting Info' },
      ],
    },
    sampleData: {
      scenarios: [
        {
          name: 'Opportunity Update — Short Fields',
          fields: {
            title: 'Acme Corp',
            subtitle: 'Opportunity Update',
            company: 'VP of Sales',
            current_amount: '$50K',
            amount: '$75K',
            current_stage: 'Proposal',
            stage: 'Negotiation',
            current_close_date: 'Mar 15',
            close_date: 'Apr 1',
          },
        },
        {
          name: 'Followup Email — Long Text',
          fields: {
            title: 'Rachel Green',
            subtitle: 'Followup Email',
            company: 'Acme Corp',
            subject: 'Re: Enterprise pricing follow-up',
            body: 'Hi Rachel, thanks for your time at the summit last week. I wanted to follow up on the enterprise pricing discussion we had after the keynote. Based on your team size of 150 and the usage patterns you described, I think our Enterprise Plus tier would be the best fit. This includes unlimited API access, dedicated support, and custom integrations. I\'ve attached a detailed proposal with three pricing options. The early-bird discount we discussed is valid through the end of this month. Would you have 30 minutes this week to walk through the numbers? I can also loop in our solutions engineer if you\'d like to discuss the technical integration path.',
          },
        },
        {
          name: 'CX Handoff — 15 Fields',
          fields: {
            title: 'Acme Corp',
            subtitle: 'CX Handoff',
            company: 'Enterprise',
            arr: '$150K',
            health: 'Green',
            csm: 'Jane Smith',
            open_tickets: '3',
            nps_score: '72',
            last_qbr: 'Jan 15, 2026',
            renewal_date: 'Jun 30, 2026',
            expansion_opp: '$45K',
            champion: 'Sarah Lin',
            exec_sponsor: 'Mark Torres',
            integration_status: 'Active',
            usage_trend: '+18% MoM',
            risk_level: 'Low',
            notes: 'Strong adoption. Ready for enterprise tier upsell.',
          },
        },
        {
          name: 'Contact Enrichment — Mixed Short + Long',
          fields: {
            title: 'Rachel Green',
            subtitle: 'Contact Enrichment',
            company: 'VP of Sales \u00b7 Acme Corp',
            email: 'rachel@acme.com',
            phone: '(555) 123-4567',
            linkedin: 'linkedin.com/in/rachelgreen',
            bio: 'Experienced enterprise sales leader with 12 years in SaaS, most recently at Datadog where she led the West Coast enterprise team to 140% quota attainment. Recently promoted to VP of Sales at Acme Corp, overseeing a team of 45 AEs across three regions.',
          },
        },
      ],
      actions: [
        { id: 'approve', label: 'Approve', isPrimary: true, interaction: 'confirm' },
        { id: 'edit', label: 'Edit', isPrimary: false, interaction: 'review' },
      ],
    },
  },
  {
    id: 'insight_panel', label: 'Insight Panel', description: 'Read-only panel for metrics and KPIs',
    supportsActions: false,
    rendersActionButtons: false,
    preview: { type: 'field_layout', layout: 'grid', emphasis: 'metrics' },
    capabilities: NO_CAPS,
    // Reserved slots must match what InsightPanel.tsx actually renders
    // (title, subtitle, category badge, body, link). Leaving this empty made
    // the wizard's Card Mapping step report "no configurable card slots", so
    // none of these fields were registered into the blueprint's data schema.
    slots: { reserved: ['title', 'subtitle', 'category', 'body', 'link'], mode: 'item-root' },
    accent: 'emerald-500',
    tableColumns: {
      columns: [
        { key: 'title', label: 'Signal', flex: true, sortable: true },
        { key: 'subtitle', label: 'Details', flex: true },
        { key: 'link', label: 'Links', width: 'w-32', align: 'right' as const },
      ],
      sampleRows: [
        { title: 'Funding Signal', subtitle: 'Series B raised by Acme Corp — $45M round led by Sequoia Capital', category: 'Insight', link: 'View Details' },
        { title: 'Leadership Change', subtitle: 'New CTO appointed at TechFlow Inc — Marcus Chen promoted fro...', category: 'Insight', link: 'View Details' },
        { title: 'Expansion Alert', subtitle: 'DataSync Labs opened 3 new offices in EMEA, hiring 200+ in Q1', category: 'Insight', link: 'View Details' },
        { title: 'Risk Signal', subtitle: 'FinBridge reported 15% revenue decline in latest quarterly filing', category: 'Insight', link: 'View Details' },
      ],
    },
    sampleData: {
      scenarios: [
        {
          name: 'Deal Velocity Alert — Short Signal',
          fields: {
            title: 'Deal Velocity Alert',
            subtitle: 'Acme Corp \u00b7 2 hours ago',
            category: 'signal',
            body: 'Deal velocity increased **32%** this week. Close date moved up from Apr 15 to Apr 1.',
          },
        },
        {
          name: 'Account Intelligence — Long Analysis',
          fields: {
            title: 'Account Intelligence Report',
            subtitle: 'Wayne Industries \u00b7 Just now',
            category: 'analysis',
            win_probability: '68%',
            was: '45%',
            engagement: 'High',
            body: 'Based on email sentiment analysis and meeting frequency data, this account shows strong buying signals. The champion (Sarah Lin, CTO) has increased internal mentions of your product by 3x in the last 2 weeks according to Gong data. However, the CFO (Mark Torres) attended the last demo call and asked pointed questions about ROI \u2014 this is typically a positive signal at this stage, indicating the deal has escalated to budget holders. Recommended next steps: prepare a custom ROI model using their actual usage data from the trial, and request a meeting specifically with Mark Torres to address financial concerns directly.',
          },
        },
      ],
    },
  },
  {
    id: 'review_card', label: 'Review Card (deprecated)', description: 'Migrated to action_card — diff display is part of ActionCard adaptive layout',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'field_layout', layout: 'horizontal', emphasis: 'data' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle'], mode: 'card' },
    tableColumns: {
      columns: [
        { key: 'title', label: 'Update', width: 'w-40', sortable: true },
        { key: 'change_1', label: 'Change 1', flex: true },
        { key: 'change_2', label: 'Change 2', flex: true },
        { key: 'more_changes', label: 'More', width: 'w-20' },
        { key: 'actions', label: 'Actions', width: 'w-28', align: 'right' as const },
      ],
      sampleRows: [
        { title: 'Acme Corp — Field Update', current_stage: 'Prospecting', stage: 'Negotiation', current_revenue: '$50K', revenue: '$150K', current_close_date: 'Mar 1', close_date: 'Apr 15' },
        { title: 'TechFlow Inc — New Contact', current_contact: '—', contact: 'Li Wei (VP Eng)', current_score: '62', score: '78' },
        { title: 'DataSync Labs — Stage Change', current_stage: 'Discovery', stage: 'Proposal', current_amount: '$80K', amount: '$120K', current_champion: 'None', champion: 'Sarah Palmer' },
        { title: 'FinBridge — Risk Flag', current_health: 'Good', health: 'At Risk', current_next_step: 'Demo', next_step: 'Exec Sponsor Call' },
      ],
    },
    sampleData: {
      scenarios: [
        { name: 'Acme Corp — Field Update', fields: { title: 'Acme Corp — Field Update', current_stage: 'Prospecting', stage: 'Negotiation', current_revenue: '$50K', revenue: '$150K', current_close_date: 'Mar 1', close_date: 'Apr 15' } },
        { name: 'TechFlow Inc — New Contact', fields: { title: 'TechFlow Inc — New Contact', current_contact: '—', contact: 'Li Wei (VP Eng)', current_score: '62', score: '78' } },
        { name: 'DataSync Labs — Stage Change', fields: { title: 'DataSync Labs — Stage Change', current_stage: 'Discovery', stage: 'Proposal', current_amount: '$80K', amount: '$120K', current_champion: 'None', champion: 'Sarah Palmer' } },
        { name: 'FinBridge — Risk Flag', fields: { title: 'FinBridge — Risk Flag', current_health: 'Good', health: 'At Risk', current_next_step: 'Demo', next_step: 'Exec Sponsor Call' } },
      ],
      actions: [
        { id: 'approve', label: 'Approve', isPrimary: true, interaction: 'confirm' },
        { id: 'edit', label: 'Edit', isPrimary: false, interaction: 'review' },
      ],
    },
  },
  {
    id: 'content_card', label: 'Content Card', description: 'Body-first card for long-form content like meeting notes, emails, and briefings',
    supportsActions: true,
    rendersActionButtons: false,
    preview: { type: 'field_layout', layout: 'vertical', emphasis: 'title' },
    capabilities: NO_CAPS,
    slots: { reserved: ['body', 'category'], mode: 'item-root' },
    tableColumns: {
      columns: [
        { key: 'title', label: 'Content', flex: true, sortable: true },
        { key: 'body', label: 'Preview', flex: true },
        { key: 'actions', label: 'Actions', width: 'w-24', align: 'right' as const },
      ],
      sampleActions: [
        { id: 'mark_read', label: 'Mark Read', isPrimary: false, interaction: 'none' },
      ],
      sampleRows: [
        { title: 'Q1 Pipeline Review', category: 'Briefing', body: 'Key talking points for the executive review: 1. Pipeline grew 23% QoQ. 2. Win rate improved from 28% to 34%...' },
        { title: 'Competitive Intel: Gong', category: 'Research', body: 'Gong released a new AI-powered forecasting feature. Their pricing model...' },
        { title: 'Account Strategy: Acme', category: 'Playbook', body: 'Multi-thread approach: engage VP Sales (champion), CRO (economic buyer)...' },
        { title: 'Objection Handling Guide', category: 'Training', body: 'Top 3 objections this quarter: 1. "Too expensive" — reframe as ROI...' },
      ],
    },
    sampleData: {
      scenarios: [
        {
          name: 'Pre-Meeting Notes',
          fields: {
            title: 'Pre-Meeting Notes',
            subtitle: 'Rawan Shaheen \u00b7 Clay (gitlab.com)',
            category: 'briefing',
            date: 'Mar 26 at 1:00 PM',
            body: '## \ud83d\udccb Account Background\n\ngitlab.com has been a Clay customer since 2024. Current ARR: $48K. Primary use case: enrichment workflows for their SDR team. Usage trending up 34% MoM.\n\n## \ud83c\udfaf Tailor\n\nFocus on expansion opportunity. Contact has shown budget authority signals with recent promotion to Director...\n\n## \ud83d\udcca Key Metrics\n\n- Current ARR: $48,000\n- Contract renewal: June 2026\n- Usage trend: +34% MoM\n- Active users: 12 of 15 seats\n\n## \u2705 Action Items\n\n1. Congratulate on promotion\n2. Discuss team growth plans for Q2\n3. Present Enterprise tier benefits',
          },
        },
        {
          name: 'Email Draft',
          fields: {
            title: 'Re: Enterprise pricing follow-up',
            subtitle: 'To: rachel.green@acme.com',
            category: 'draft',
            body: 'Hi Rachel,\n\nThanks for your time at the Pavilion CRO Summit. I wanted to follow up on our conversation about enterprise pricing for your team of 50+ users...',
          },
        },
        {
          name: 'Support Digest',
          fields: {
            title: 'Weekly Support Summary \u2014 Acme Corp',
            subtitle: '3 tickets \u00b7 2 resolved \u00b7 1 pending',
            category: 'digest',
            body: 'TICK-4521|Login SSO redirect loop|resolved\nTICK-4523|API rate limit confusion|resolved\nTICK-4527|Custom field sync delay|pending',
            sentiment: 'Positive',
            tickets: '[{"id":"TICK-4521","desc":"Login SSO redirect loop","status":"resolved"},{"id":"TICK-4523","desc":"API rate limit confusion","status":"resolved"},{"id":"TICK-4527","desc":"Custom field sync delay","status":"pending"}]',
          },
        },
      ],
      actions: [
        { id: 'mark_read', label: 'Mark Read', isPrimary: true, interaction: 'none' },
        { id: 'edit', label: 'Edit Draft', isPrimary: false, interaction: 'review' },
        { id: 'send', label: 'Send', isPrimary: false, interaction: 'confirm' },
      ],
    },
  },
  {
    id: 'data_card', label: 'Data Card', description: 'Metric-focused card with headline number, stage badge, and supporting metadata',
    supportsActions: true,
    rendersActionButtons: true,
    preview: { type: 'field_layout', layout: 'horizontal', emphasis: 'metrics' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'metric', 'stage', 'score'], mode: 'card' },
    tableColumns: {
      columns: [
        { key: 'title', label: 'Opportunity', flex: true, sortable: true },
        { key: 'metric', label: 'Amount', width: 'w-24', sortable: true },
        { key: 'stage', label: 'Stage', width: 'w-24', sortable: true },
        { key: 'score', label: 'Score', width: 'w-24', sortable: true },
        { key: 'date', label: 'Close Date', width: 'w-24', sortable: true },
        { key: 'actions', label: 'Actions', width: 'w-20', align: 'right' as const },
      ],
      sampleRows: [
        { title: 'Acme Corp — Enterprise', metric: '$150,000', stage: 'Negotiation', score: '87', date: 'Apr 15' },
        { title: 'TechFlow Inc — Mid-Market', metric: '$65,000', stage: 'Discovery', score: '62', date: 'May 1' },
        { title: 'DataSync Labs — Enterprise', metric: '$120,000', stage: 'Proposal', score: '78', date: 'Mar 28' },
        { title: 'FinBridge — SMB', metric: '$28,000', stage: 'Closed Won', score: '95', date: 'Mar 10' },
      ],
    },
    sampleData: {
      scenarios: [
        {
          name: 'Pipeline Health — Single Metric',
          fields: {
            title: 'Pipeline Health',
            metric: '$1.2M',
            subtitle: '+18% vs last quarter',
          },
        },
        {
          name: 'Q1 Pipeline Summary — Metric + Supporting Fields',
          fields: {
            title: 'Q1 Pipeline Summary',
            metric: '$1.2M',
            subtitle: '+18% vs last quarter',
            deals_in_pipeline: '34',
            avg_deal_size: '$35K',
            win_rate: '28%',
            avg_cycle: '42 days',
            at_risk: '6 deals',
            closing_this_month: '8 deals',
          },
        },
      ],
      actions: [
        { id: 'open_link', label: 'View Pipeline', isPrimary: true, interaction: 'none' },
        { id: 'summarize_bundle', label: 'Export', isPrimary: false, interaction: 'confirm' },
      ],
    },
  },
  {
    id: 'brief_card', label: 'Brief Card', description: 'Pre-meeting Deal Brief: mode-aware above-the-fold summary with source-tagged facts and expandable detail',
    supportsActions: true,
    rendersActionButtons: true,
    preview: { type: 'field_layout', layout: 'vertical', emphasis: 'title' },
    capabilities: NO_CAPS,
    // Reserved slots must match what BriefCard.tsx actually renders (same rule
    // as insight_panel): title, subtitle, the scalar mode badge, and the
    // JSON-stringified DealBrief under `brief`.
    slots: { reserved: ['title', 'subtitle', 'mode', 'brief'], mode: 'item-root' },
    accent: 'blueberry-500',
    tableColumns: {
      columns: [
        { key: 'title', label: 'Meeting', flex: true, sortable: true },
        { key: 'mode', label: 'Mode', width: 'w-28', sortable: true },
        { key: 'updated', label: 'Updated', width: 'w-24', sortable: true },
        { key: 'actions', label: 'Actions', width: 'w-28', align: 'right' as const },
      ],
      sampleRows: [
        { title: 'ImageTrend · Meeting #4', mode: 'Close loops', updated: 'Today' },
        { title: 'Photoroom · Meeting #1', mode: 'Find out', updated: 'Today' },
        { title: 'Shepper · Meeting #1', mode: 'Qualify', updated: 'Yesterday' },
      ],
    },
    sampleData: {
      scenarios: [
        {
          name: 'Deal — ImageTrend (close loops)',
          fields: {
            title: 'ImageTrend × Clay — pricing & security review',
            subtitle: 'ImageTrend · Meeting #4 · 1. Qualification',
            mode: 'deal',
            brief: dealBriefSampleJson('deal'),
          },
        },
        {
          name: 'Discovery — Photoroom (find out)',
          fields: {
            title: 'Photoroom / Clay — intro & discovery',
            subtitle: 'Photoroom · Meeting #1',
            mode: 'discovery',
            brief: dealBriefSampleJson('discovery'),
          },
        },
        {
          name: 'Inbound — Shepper (qualify)',
          fields: {
            title: 'Shepper — inbound demo',
            subtitle: 'Shepper · Meeting #1',
            mode: 'inbound',
            brief: dealBriefSampleJson('inbound'),
          },
        },
      ],
      actions: [
        { id: 'regenerate', label: 'Regenerate', isPrimary: false, interaction: 'confirm' },
        { id: 'mark_read', label: 'Mark Read', isPrimary: true, interaction: 'none' },
      ],
    },
  },

  // ── Utility card types (not deprecated, still actively used) ──
  {
    id: 'event_card', label: 'Event Card', description: 'Calendar event card with date, time, and attendee details',
    supportsActions: true,
    rendersActionButtons: true,
    preview: { type: 'field_layout', layout: 'horizontal', emphasis: 'data' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'date', 'time', 'attendee', 'location'], mode: 'card' },
  },
  {
    id: 'list_row', label: 'List Row', description: 'Compact row for split-panel reading',
    supportsActions: false,
    rendersActionButtons: false,
    preview: { type: 'template_card', showActions: false, showFields: true },
    capabilities: { supportsSelection: true, supportsKeyboardNav: true, supportsBulkActions: false },
    slots: { reserved: ['title', 'subtitle', 'timestamp', 'company', 'avatar'], mode: 'card' },
  },
  {
    id: 'check_row', label: 'Check Row', description: 'Checkbox row for batch selection',
    supportsActions: false,
    rendersActionButtons: false,
    preview: { type: 'template_card', showActions: false, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'company', 'avatar'], mode: 'card' },
  },
  {
    id: 'batch_card', label: 'Batch Card', description: 'Checkbox selection card for batch processing workflows',
    supportsActions: false,
    rendersActionButtons: false,
    preview: { type: 'template_card', showActions: false, showFields: true },
    capabilities: { supportsSelection: true, supportsKeyboardNav: false, supportsBulkActions: true },
    slots: { reserved: ['title', 'subtitle', 'company', 'avatar', 'priority', 'status'], mode: 'card' },
  },

  // ── Deprecated card types ─────────────────────────────────
  // Kept for isValidRegistryId() during the migration transition window.
  // Migration 20260326_migrate_card_types remaps all DB rows to canonical types.
  // These entries can be removed once the migration has run in all environments.
  {
    id: 'multi_action_card', label: 'Multi-Action Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'link', 'company', 'avatar', 'priority'], mode: 'card' },
  },
  {
    id: 'generic_card', label: 'Generic Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: false, rendersActionButtons: false, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: false, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'company', 'avatar'], mode: 'card' },
  },
  {
    id: 'triage_card', label: 'Triage Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'link', 'company', 'avatar', 'priority'], mode: 'card' },
  },
  {
    id: 'focus_card', label: 'Focus Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'link', 'company', 'avatar', 'priority'], mode: 'card' },
  },
  {
    id: 'ops_card', label: 'Operations Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: { supportsSelection: false, supportsKeyboardNav: true, supportsBulkActions: false },
    slots: { reserved: ['title', 'subtitle', 'status', 'company', 'avatar', 'priority'], mode: 'card' },
  },
  {
    id: 'kanban_card', label: 'Kanban Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'company', 'avatar', 'priority', 'status'], mode: 'card' },
  },
  {
    id: 'contact_card', label: 'Contact Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'field_layout', layout: 'horizontal', emphasis: 'title' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'link', 'score'], mode: 'card' },
  },
  {
    id: 'smart_bundle_card', label: 'Smart Bundle Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'company', 'avatar', 'priority', 'status'], mode: 'card' },
  },
  {
    id: 'quick_glance_card', label: 'Quick Glance Card (deprecated)', description: 'Migrated to action_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'action_card',
    preview: { type: 'template_card', showActions: true, showFields: true },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'company', 'avatar', 'priority', 'status'], mode: 'card' },
  },
  {
    id: 'deal_card', label: 'Deal Card (deprecated)', description: 'Migrated to data_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'data_card',
    preview: { type: 'field_layout', layout: 'horizontal', emphasis: 'data' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'metric', 'stage'], mode: 'card' },
  },
  {
    id: 'scorecard_card', label: 'Scorecard Card (deprecated)', description: 'Migrated to data_card',
    supportsActions: true, rendersActionButtons: true, deprecated: true, migratesTo: 'data_card',
    preview: { type: 'field_layout', layout: 'horizontal', emphasis: 'metrics' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle', 'score'], mode: 'card' },
  },
  {
    id: 'briefing_card', label: 'Briefing Card (deprecated)', description: 'Migrated to content_card',
    supportsActions: true, rendersActionButtons: false, deprecated: true, migratesTo: 'content_card',
    preview: { type: 'field_layout', layout: 'vertical', emphasis: 'title' },
    capabilities: NO_CAPS,
    slots: { reserved: ['body'], mode: 'item-root' },
  },
  {
    id: 'email_card', label: 'Email Card (deprecated)', description: 'Migrated to content_card',
    supportsActions: true, rendersActionButtons: false, deprecated: true, migratesTo: 'content_card',
    preview: { type: 'field_layout', layout: 'vertical', emphasis: 'title' },
    capabilities: NO_CAPS,
    slots: { reserved: ['body'], mode: 'item-root' },
  },
  {
    id: 'digest_card', label: 'Digest Card (deprecated)', description: 'Migrated to insight_panel',
    supportsActions: false, rendersActionButtons: false, deprecated: true, migratesTo: 'insight_panel',
    preview: { type: 'field_layout', layout: 'vertical', emphasis: 'metrics' },
    capabilities: NO_CAPS,
    slots: { reserved: ['title', 'subtitle'], mode: 'card' },
  },
] as const satisfies readonly ArtifactCardEntry[];

export type ArtifactCard = (typeof ARTIFACT_CARD_REGISTRY)[number]['id'];

/** Get the active (non-deprecated) card types only */
export const ACTIVE_ARTIFACT_CARDS: readonly ArtifactCardEntry[] =
  (ARTIFACT_CARD_REGISTRY as readonly ArtifactCardEntry[]).filter((e) => !e.deprecated);

/**
 * True when `id` is a registered artifact card (deprecated ones included).
 * Not `isArtifactCard` in `schemas/base/artifact-meta.ts`, which checks an
 * older, different list.
 */
export function isArtifactCardId(id: string): id is ArtifactCard {
  return (ARTIFACT_CARD_REGISTRY as readonly ArtifactCardEntry[]).some((e) => e.id === id);
}

export function getArtifactCardEntry(id: ArtifactCard | (string & {})): ArtifactCardEntry | undefined {
  return (ARTIFACT_CARD_REGISTRY as readonly ArtifactCardEntry[]).find((e) => e.id === id);
}
